/* Kora core — no dependencies. Loaded once, deferred, on every page.
   Provides: dialogs, ajax add-to-cart, cart count, carousel arrows, scroll reveal and small helpers on window.Kora. */
(() => {
  const K = (window.Kora = window.Kora || {});

  /* ---------- helpers ---------- */
  K.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };

  K.announce = (message) => {
    const el = document.getElementById('Announcer');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => { el.textContent = message; });
  };

  K.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* Fetch the rendered HTML of one section for a given page URL (Section Rendering API). */
  K.fetchSection = async (url, sectionId) => {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error(res.statusText);
    return K.parseHTML(await res.text());
  };

  /* ---------- dialogs ---------- */
  K.openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('is-locked');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    dialog._trigger = trigger;
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  };

  K.closeDialog = (dialog) => { if (dialog && dialog.open) dialog.close(); };

  document.addEventListener('close', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
    if (d._trigger) d._trigger.setAttribute('aria-expanded', 'false');
  }, true);

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-dialog-open]');
    if (open) {
      const dialog = document.querySelector(open.dataset.dialogOpen);
      if (dialog) { e.preventDefault(); K.openDialog(dialog, open); }
      return;
    }
    const close = e.target.closest('[data-dialog-close]');
    if (close) { const d = close.closest('dialog'); if (d) d.close(); return; }
    if (e.target instanceof HTMLDialogElement && e.target.open) e.target.close(); /* backdrop click */

    const cartLink = e.target.closest('[data-cart-toggle]');
    if (cartLink && K.cartType === 'drawer' && K.template !== 'cart') {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { detail: { trigger: cartLink } }));
    }
  });

  /* ---------- cart ---------- */
  K.cart = {
    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count > 99 ? '99+' : count;
        el.hidden = !count;
      });
    },
    /* Reads the item count from a rendered cart section (<... data-cart-meta data-item-count="N">) */
    countFromSections(sections) {
      const html = sections && (sections['cart-drawer'] || Object.values(sections)[0]);
      if (!html) return null;
      const meta = K.parseHTML(html).querySelector('[data-cart-meta]');
      return meta ? Number(meta.dataset.itemCount) : null;
    },
    async add(formData, sectionIds = ['cart-drawer']) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', location.pathname);
      const res = await fetch(`${K.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || K.strings.error);
      return data;
    }
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest && e.target.closest('form[data-cart-add]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[data-add-button]');
    if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) return;
    const label = form.querySelector('[data-add-label]');
    const errorBox = form.querySelector('[data-form-error]');
    if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
    if (button) button.classList.add('is-loading');

    try {
      const data = await K.cart.add(new FormData(form));
      const count = K.cart.countFromSections(data.sections);
      if (count !== null) K.cart.setCount(count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections, itemCount: count, source: 'add' } }));
      if (K.cartType === 'page' || K.template === 'cart') {
        if (K.template !== 'cart') { location.href = K.routes.cart; return; }
        location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { detail: { trigger: button } }));
      if (label) { const old = label.textContent; label.textContent = K.strings.added; setTimeout(() => { label.textContent = old; }, 1800); }
      K.announce(K.strings.added);
    } catch (err) {
      if (errorBox) { errorBox.textContent = err.message; errorBox.hidden = false; }
      else K.announce(err.message);
    } finally {
      if (button) button.classList.remove('is-loading');
    }
  });

  /* ---------- carousel arrows: <kora-carousel> wraps .carousel__track ---------- */
  class KoraCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: 'smooth' });
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.update = K.debounce(() => this.sync(), 60);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update, { passive: true });
      this.sync();
    }
    disconnectedCallback() { window.removeEventListener('resize', this.update); }
    sync() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = Math.abs(this.track.scrollLeft) < 2;
      this.next.disabled = Math.abs(this.track.scrollLeft) >= max;
      const nav = this.querySelector('.carousel__nav');
      if (nav) nav.style.visibility = max <= 0 ? 'hidden' : '';
    }
  }
  if (!customElements.get('kora-carousel')) customElements.define('kora-carousel', KoraCarousel);

  /* ---------- reveal on scroll ---------- */
  const initReveal = () => {
    const items = document.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) { items.forEach((i) => i.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((i) => io.observe(i));
  };
  document.addEventListener('DOMContentLoaded', initReveal);
  document.addEventListener('shopify:section:load', initReveal);
})();
